'use client'

import { forwardRef, useState, useRef, useEffect, useMemo } from 'react'
import { Search, ChevronDown, X, Check, AlertTriangle } from 'lucide-react'
import { safeAccess, safeArrayAccess } from '../utils/security'

/**
 * BIRHAUS Combobox Component
 * 
 * Enhanced combobox implementing BIRHAUS Principle #1: Miller's Law (7±2)
 * 
 * Features:
 * - Maximum 7 visible options for cognitive load optimization
 * - Intelligent filtering and search
 * - Spanish-first labeling
 * - Progressive disclosure for large option sets
 * - Accessibility-first design (WCAG AA+)
 */

// BIRHAUS: Utility function (self-contained)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

export interface BirhausComboboxOption {
  value: string
  label: string
  labelEs?: string
  labelEn?: string
  description?: string
  descriptionEs?: string
  descriptionEn?: string
  disabled?: boolean
  group?: string
  icon?: React.ReactNode
  searchTerms?: string[] // Additional terms for filtering
}

export interface BirhausComboboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  // Options
  options: BirhausComboboxOption[]
  
  // Value handling
  value?: string
  onValueChange?: (value: string) => void
  
  // BIRHAUS Principle #7: Bilingual by design
  placeholder?: string
  placeholderEs?: string
  placeholderEn?: string
  
  emptyMessage?: string
  emptyMessageEs?: string
  emptyMessageEn?: string
  
  searchPlaceholder?: string
  searchPlaceholderEs?: string
  searchPlaceholderEn?: string
  
  // Miller's Law enforcement
  maxVisibleOptions?: number
  showCognitiveWarning?: boolean
  
  // Behavior
  searchable?: boolean
  clearable?: boolean
  disabled?: boolean
  loading?: boolean
  
  // Filtering
  filterFunction?: (option: BirhausComboboxOption, search: string) => boolean
  
  // Styling
  variant?: 'default' | 'success' | 'warning' | 'error'
  size?: 'sm' | 'md' | 'lg'
  
  // Error handling
  error?: boolean
  errorMessage?: string
  errorMessageEs?: string
  errorMessageEn?: string
  
  // Validation
  required?: boolean
  
  // Positioning
  dropdownPosition?: 'bottom' | 'top' | 'auto'
  
  // Styling
  className?: string
  dropdownClassName?: string
}

export const BirhausCombobox = forwardRef<HTMLInputElement, BirhausComboboxProps>(({
  // Options
  options,
  
  // Value
  value,
  onValueChange,
  
  // Content (Spanish-first)
  placeholder,
  placeholderEs,
  placeholderEn,
  emptyMessage,
  emptyMessageEs,
  emptyMessageEn,
  searchPlaceholder,
  searchPlaceholderEs,
  searchPlaceholderEn,
  
  // Miller's Law settings
  maxVisibleOptions = 7,
  showCognitiveWarning = true,
  
  // Behavior
  searchable = true,
  clearable = true,
  disabled = false,
  loading = false,
  
  // Filtering
  filterFunction,
  
  // Styling
  variant = 'default',
  size = 'md',
  
  // Error handling
  error = false,
  errorMessage,
  errorMessageEs,
  errorMessageEn,
  
  // Validation
  required = false,
  
  // Positioning
  dropdownPosition = 'auto',
  
  // Styling
  className,
  dropdownClassName,
  
  // Standard props
  ...props
}, ref) => {
  
  // BIRHAUS: State management
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const [internalValue, setInternalValue] = useState(value || '')
  
  const currentValue = value !== undefined ? value : internalValue
  const isControlled = value !== undefined
  
  // Refs
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const resolveText = (spanish?: string, english?: string, fallback?: string) => {
    return spanish || english || fallback || ''
  }
  
  const displayPlaceholder = resolveText(placeholderEs, placeholderEn, placeholder || 'Seleccionar opción...')
  const displayEmptyMessage = resolveText(emptyMessageEs, emptyMessageEn, emptyMessage || 'No se encontraron opciones')
  const displaySearchPlaceholder = resolveText(searchPlaceholderEs, searchPlaceholderEn, searchPlaceholder || 'Buscar...')
  const displayErrorMessage = resolveText(errorMessageEs, errorMessageEn, errorMessage)
  
  // BIRHAUS: Default filter function
  const defaultFilter = (option: BirhausComboboxOption, search: string) => {
    const searchLower = search.toLowerCase()
    const label = resolveText(option.labelEs, option.labelEn, option.label).toLowerCase()
    const description = resolveText(option.descriptionEs, option.descriptionEn, option.description)?.toLowerCase() || ''
    const additionalTerms = option.searchTerms?.join(' ').toLowerCase() || ''
    
    return label.includes(searchLower) || 
           description.includes(searchLower) || 
           additionalTerms.includes(searchLower)
  }
  
  const filterFunc = filterFunction || defaultFilter
  
  // BIRHAUS: Filtered and visible options
  const { filteredOptions, visibleOptions, hiddenCount, cognitiveLoad } = useMemo(() => {
    const filtered = options.filter(option => 
      !option.disabled && 
      (searchTerm === '' || filterFunc(option, searchTerm))
    )
    
    const visible = filtered.slice(0, maxVisibleOptions)
    const hidden = filtered.length - visible.length
    
    const load = visible.length > 7 ? 'high' : 
                 visible.length > 5 ? 'medium' : 'low'
    
    return {
      filteredOptions: filtered,
      visibleOptions: visible,
      hiddenCount: hidden,
      cognitiveLoad: load
    }
  }, [options, searchTerm, maxVisibleOptions, filterFunc])
  
  // BIRHAUS: Warn about cognitive overload
  if (showCognitiveWarning && visibleOptions.length > 7) {
    console.warn(`BIRHAUS Miller's Law Warning: Combobox shows ${visibleOptions.length} options (recommended: max 7)`)
  }
  
  // BIRHAUS: Get selected option
  const selectedOption = options.find(option => option.value === currentValue)
  
  // BIRHAUS: Handle value change
  const handleValueChange = (newValue: string) => {
    if (!isControlled) {
      setInternalValue(newValue)
    }
    onValueChange?.(newValue)
    setIsOpen(false)
    setSearchTerm('')
    setFocusedIndex(-1)
  }
  
  // BIRHAUS: Handle clear
  const handleClear = () => {
    handleValueChange('')
  }
  
  // BIRHAUS: Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return
    
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        if (!isOpen) {
          setIsOpen(true)
        } else {
          setFocusedIndex(prev => 
            prev < visibleOptions.length - 1 ? prev + 1 : 0
          )
        }
        break
        
      case 'ArrowUp':
        event.preventDefault()
        if (isOpen) {
          setFocusedIndex(prev => 
            prev > 0 ? prev - 1 : visibleOptions.length - 1
          )
        }
        break
        
      case 'Enter':
        event.preventDefault()
        if (isOpen && focusedIndex >= 0) {
          const option = safeArrayAccess(visibleOptions, focusedIndex, null)
          if (option) {
            handleValueChange(option.value)
          }
        } else {
          setIsOpen(!isOpen)
        }
        break
        
      case 'Escape':
        setIsOpen(false)
        setFocusedIndex(-1)
        inputRef.current?.blur()
        break
        
      case 'Tab':
        setIsOpen(false)
        break
    }
  }
  
  // BIRHAUS: Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setFocusedIndex(-1)
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])
  
  // BIRHAUS Principle #9: Consistency via tokens
  const sizeClasses = {
    sm: {
      input: 'px-3 py-1.5 text-sm',
      option: 'px-3 py-1.5 text-sm',
      icon: 'h-4 w-4'
    },
    md: {
      input: 'px-4 py-2 text-base',
      option: 'px-4 py-2 text-base',
      icon: 'h-5 w-5'
    },
    lg: {
      input: 'px-5 py-3 text-lg',
      option: 'px-5 py-3 text-lg',
      icon: 'h-6 w-6'
    }
  }
  
  const variantClasses = {
    default: {
      border: 'border-gray-300',
      focus: 'focus:border-blue-500 focus:ring-blue-500'
    },
    success: {
      border: 'border-green-300',
      focus: 'focus:border-green-500 focus:ring-green-500'
    },
    warning: {
      border: 'border-yellow-300',
      focus: 'focus:border-yellow-500 focus:ring-yellow-500'
    },
    error: {
      border: 'border-red-300',
      focus: 'focus:border-red-500 focus:ring-red-500'
    }
  }
  
  const currentSize = safeAccess(sizeClasses, size, sizeClasses.md)
  const currentVariant = safeAccess(variantClasses, error ? 'error' : variant, variantClasses.default)
  
  const inputClasses = cn(
    // Base styles
    'w-full rounded-lg border bg-white transition-colors duration-200',
    'focus:outline-none focus:ring-2 focus:ring-offset-0',
    
    // Size
    currentSize.input,
    
    // Variant
    currentVariant.border,
    currentVariant.focus,
    
    // State
    disabled && 'bg-gray-50 text-gray-400 cursor-not-allowed',
    
    // Custom classes
    className
  )
  
  return (
    <div className="space-y-2">
      {/* BIRHAUS: Cognitive load warning */}
      {showCognitiveWarning && cognitiveLoad === 'high' && searchTerm && (
        <div className="flex items-center gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <span className="text-xs text-yellow-800">
            Mostrando {visibleOptions.length} opciones (máximo recomendado: 7).
            {hiddenCount > 0 && ` ${hiddenCount} opciones ocultas.`}
          </span>
        </div>
      )}
      
      {/* Combobox container */}
      <div ref={containerRef} className="relative">
        {/* Input */}
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={searchable && isOpen ? searchTerm : (selectedOption ? resolveText(selectedOption.labelEs, selectedOption.labelEn, selectedOption.label) : '')}
            onChange={(e) => {
              if (searchable) {
                setSearchTerm(e.target.value)
                setIsOpen(true)
                setFocusedIndex(-1)
              }
            }}
            onFocus={() => !disabled && setIsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={displayPlaceholder}
            disabled={disabled}
            required={required}
            className={inputClasses}
            role="combobox"
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-owns="combobox-options"
            {...props}
          />
          
          {/* Icons */}
          <div className="absolute inset-y-0 right-0 flex items-center gap-1 pr-3">
            {loading && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
            )}
            
            {clearable && currentValue && !loading && (
              <button
                onClick={handleClear}
                disabled={disabled}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                aria-label="Limpiar selección"
              >
                <X className={currentSize.icon} />
              </button>
            )}
            
            <button
              onClick={() => !disabled && setIsOpen(!isOpen)}
              disabled={disabled}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
              aria-label={isOpen ? 'Cerrar opciones' : 'Abrir opciones'}
            >
              <ChevronDown className={cn(
                currentSize.icon,
                'transition-transform duration-200',
                isOpen && 'transform rotate-180'
              )} />
            </button>
          </div>
        </div>
        
        {/* Dropdown */}
        {isOpen && (
          <div 
            ref={dropdownRef}
            className={cn(
              'absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto',
              dropdownPosition === 'top' && 'bottom-full mb-1 mt-0',
              dropdownClassName
            )}
            id="combobox-options"
            role="listbox"
          >
            {visibleOptions.length === 0 ? (
              <div className={cn(
                'text-gray-500 text-center',
                currentSize.option
              )}>
                {displayEmptyMessage}
              </div>
            ) : (
              <>
                {visibleOptions.map((option, index) => {
                  const displayLabel = resolveText(option.labelEs, option.labelEn, option.label)
                  const displayDescription = resolveText(option.descriptionEs, option.descriptionEn, option.description)
                  const isSelected = option.value === currentValue
                  const isFocused = index === focusedIndex
                  
                  return (
                    <div
                      key={option.value}
                      onClick={() => handleValueChange(option.value)}
                      className={cn(
                        'cursor-pointer flex items-center gap-3 transition-colors duration-150',
                        currentSize.option,
                        
                        // State styles
                        isSelected && 'bg-blue-50 text-blue-700',
                        isFocused && 'bg-gray-100',
                        !isSelected && !isFocused && 'hover:bg-gray-50',
                        
                        // Group separation
                        index > 0 && visibleOptions[index - 1]?.group !== option.group && 'border-t border-gray-100'
                      )}
                      role="option"
                      aria-selected={isSelected}
                      data-value={option.value}
                    >
                      {/* Icon */}
                      {option.icon && (
                        <span className="flex-shrink-0 text-gray-400">
                          {option.icon}
                        </span>
                      )}
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{displayLabel}</div>
                        {displayDescription && (
                          <div className="text-sm text-gray-500 truncate">
                            {displayDescription}
                          </div>
                        )}
                      </div>
                      
                      {/* Selected indicator */}
                      {isSelected && (
                        <Check className="h-4 w-4 text-blue-600 flex-shrink-0" />
                      )}
                    </div>
                  )
                })}
                
                {/* Show more indicator */}
                {hiddenCount > 0 && (
                  <div className={cn(
                    'text-gray-500 text-center border-t border-gray-100 bg-gray-50',
                    currentSize.option
                  )}>
                    +{hiddenCount} opciones más (refina la búsqueda)
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
      
      {/* Error message */}
      {error && displayErrorMessage && (
        <div className="text-red-600 text-sm flex items-center gap-1">
          <span>{displayErrorMessage}</span>
        </div>
      )}
    </div>
  )
})

BirhausCombobox.displayName = 'BirhausCombobox'

// BIRHAUS: Hook for managing combobox state
export function useBirhausCombobox(initialValue = '') {
  const [value, setValue] = useState(initialValue)
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  
  const reset = () => {
    setValue(initialValue)
    setIsOpen(false)
    setSearchTerm('')
  }
  
  return {
    value,
    setValue,
    isOpen,
    setIsOpen,
    searchTerm,
    setSearchTerm,
    reset
  }
}

// BIRHAUS: Helper to create combobox options with Spanish-first labels
export function createBirhausComboboxOption(
  value: string,
  labelEs: string,
  options?: {
    labelEn?: string
    descriptionEs?: string
    descriptionEn?: string
    disabled?: boolean
    group?: string
    icon?: React.ReactNode
    searchTerms?: string[]
  }
): BirhausComboboxOption {
  return {
    value,
    label: labelEs,
    labelEs,
    labelEn: options?.labelEn,
    descriptionEs: options?.descriptionEs,
    descriptionEn: options?.descriptionEn,
    disabled: options?.disabled || false,
    group: options?.group,
    icon: options?.icon,
    searchTerms: options?.searchTerms
  }
}

// Components and utilities are exported via individual export declarations above